import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { FaFilter } from 'react-icons/fa';
import UiSelect from '../components/UiSelect.jsx';
import { PaymentsTable } from '../tables/PageTables.jsx';
import {
  getPayments, getPaymentsStats, getDashboardData, getLibraries, getStudents,
  getSeatsData, getStudentPaymentPlans, getStudentOutstanding, createPayment
} from '../services/api.js';
import { downloadPaymentReceiptPdf, downloadCombinedPaymentReceiptPdf } from '../services/api.js';
import { sendPaymentReceiptPdfWhatsApp, sendStudentPaymentHistoryWhatsApp } from '../api/api1.js';
import axios from '../api';
import { useUrlFilters, readUrlFilters, intParam } from '../hooks/useUrlFilters.js';
import '../styles/pages/payments.css';

const extractList = (response, keys = []) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  for (const key of keys) {
    if (Array.isArray(response[key])) return response[key];
    if (Array.isArray(response.data?.[key])) return response.data[key];
  }
  if (Array.isArray(response.data)) return response.data;
  if (Array.isArray(response.results)) return response.results;
  return [];
};

export function PaymentsPage({ toast, receiptPickerOpen = false, onCloseReceiptPicker = () => {}, initialStudentId = null }) {
  const [payments, setPayments] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [revenueStats, setRevenueStats] = useState(null);
  const [outstandingMap, setOutstandingMap] = useState({});
  const [libraries, setLibraries] = useState([]);
  const [students, setStudents] = useState([]);
  const [seatLibraryMap, setSeatLibraryMap] = useState({});
  const [generating, setGenerating] = useState(false);
  const [studentLoading, setStudentLoading] = useState(false);
  const [savingPayment, setSavingPayment] = useState(false);
  const [downloadingReceiptId, setDownloadingReceiptId] = useState(null);
  const [sendingHistoryId, setSendingHistoryId] = useState(null);

  const initialUrlFilters = useMemo(() => readUrlFilters({
    search: { defaultValue: '' },
    status: { defaultValue: 'All' },
    library: { defaultValue: 'All' },
    libraryId: { defaultValue: '' },
    student: { defaultValue: '' },
    studentName: { defaultValue: '' },
    start: { defaultValue: '' },
    end: { defaultValue: '' },
    view: { defaultValue: 'all' },
    page: { defaultValue: 1, parse: intParam(1, 100000) },
    perPage: { defaultValue: 10, parse: intParam(1, 100) },
  }), []);
  const [filters, setFilters] = useState(() => ({
    search: '',
    status: initialUrlFilters.status,
    library: initialUrlFilters.library,
    libraryId: initialUrlFilters.libraryId,
    studentId: initialUrlFilters.student,
    studentName: initialUrlFilters.studentName,
    start: initialUrlFilters.start,
    end: initialUrlFilters.end
  }));
  const [showPaymentFilters, setShowPaymentFilters] = useState(false);
  const [filter, setFilter] = useState(() => initialUrlFilters.view);
  const [searchQuery, setSearchQuery] = useState(() => initialUrlFilters.search);
  const [currentPage, setCurrentPage] = useState(() => initialUrlFilters.page);
  const [rowsPerPage, setRowsPerPage] = useState(() => initialUrlFilters.perPage);
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [studentSearch, setStudentSearch] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [studentPlans, setStudentPlans] = useState([]);
  const [outstandingInfo, setOutstandingInfo] = useState(null);
  const [seatUpgrade, setSeatUpgrade] = useState(null);
  const [canViewFinancials] = useState(() => {
    try {
      const user = JSON.parse(localStorage.getItem('userinfo') || '{}');
      return String(user.role || '').toLowerCase().replace(/[\s_-]/g, '') === 'tenantowner';
    } catch (error) {
      console.error('Failed to read current user role:', error);
      return false;
    }
  });
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedPlanAmount, setSelectedPlanAmount] = useState(null);
  const [formData, setFormData] = useState({
    studentid:'', planid:'', amount:'', receivedAmount:'',
    paymentmethod:'cash', transaction_id:'', status:'completed',
    sendReminder:false, renewalRequested:false,
    paymentType:'subscription', upgradeAmount:'', subscriptionAmount:'', payBoth:false
  });
  const [errors, setErrors] = useState({});
  const handledInitialStudent = useRef(null);
  const initialPaymentsLoaded = useRef(false);
  const studentRequestId = useRef(0);
  const paymentRequestId = useRef(0);
  const lastFetchKey = useRef('');

  const setFilterField = (key) => (update) => setFilters(previous => {
    const nextValue = typeof update === 'function' ? update(previous[key]) : update;
    return Object.is(previous[key], nextValue) ? previous : { ...previous, [key]: nextValue };
  });

  useUrlFilters([
    { key: 'search', value: searchQuery, setValue: setSearchQuery, defaultValue: '' },
    { key: 'status', value: filters.status, setValue: setFilterField('status'), defaultValue: 'All' },
    { key: 'library', value: filters.library, setValue: setFilterField('library'), defaultValue: 'All' },
    { key: 'libraryId', value: filters.libraryId, setValue: setFilterField('libraryId'), defaultValue: '' },
    { key: 'student', value: filters.studentId, setValue: setFilterField('studentId'), defaultValue: '' },
    { key: 'studentName', value: filters.studentName, setValue: setFilterField('studentName'), defaultValue: '' },
    { key: 'start', value: filters.start, setValue: setFilterField('start'), defaultValue: '' },
    { key: 'end', value: filters.end, setValue: setFilterField('end'), defaultValue: '' },
    { key: 'view', value: filter, setValue: setFilter, defaultValue: 'all' },
    { key: 'page', value: currentPage, setValue: setCurrentPage, defaultValue: 1, parse: intParam(1, 100000) },
    { key: 'perPage', value: rowsPerPage, setValue: setRowsPerPage, defaultValue: 10, parse: intParam(1, 100) },
  ]);

  const paymentDate = payment => payment?.payment_date || payment?.paymentdate || payment?.paymentDate || payment?.date || payment?.created_at;
  const currency = value => `\u20B9${Number(value||0).toLocaleString('en-IN')}`;
  const dateKey=value=>{
    const date=new Date(value);
    if(Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
  };
  const formatPaymentDate=value=>{
    const date=new Date(value);
    return Number.isNaN(date.getTime())?'-':date.toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'});
  };

  const fetchLive = useCallback(async (customFilters=filters, quickFilter=filter, query=searchQuery) => {
    const requestId=++paymentRequestId.current;
    lastFetchKey.current=JSON.stringify([customFilters,quickFilter,query]);
    setGenerating(true);
    try {
      const params={};
      if(customFilters.libraryId) params.library_id=customFilters.libraryId;
      if(customFilters.studentId) params.student_id=customFilters.studentId;
      if(customFilters.status!=='All') params.status=customFilters.status.toLowerCase();
      if(query.trim()) params.search=query.trim();
      const today=new Date();
      if(quickFilter==='today'){ params.start_date=dateKey(today); params.end_date=dateKey(today); }
      else if(quickFilter==='this-month'){ const first=new Date(today.getFullYear(),today.getMonth(),1); params.start_date=dateKey(first); params.end_date=dateKey(today); }
      else if(quickFilter==='pending'){ /* Pending mode lists students with dues, not pending payment records. */ }
      else { if(customFilters.start) params.start_date=customFilters.start; if(customFilters.end) params.end_date=customFilters.end; }

      const results = await Promise.allSettled([
        getPayments(params, true),
        getDashboardData(customFilters.libraryId || null, true),
        getLibraries(),
        getStudents(null, customFilters.libraryId || null),
        getSeatsData(),
        getPaymentsStats(customFilters.libraryId || null, true)
      ]);
      if(requestId!==paymentRequestId.current) return;
      if (results[0].status === 'rejected') throw results[0].reason;
      const [payRes, dashResult, libResult, studResult, seatsResult, statsResult] = results;
      const payData=extractList(payRes.value, ['payments']);
      const dashRes=dashResult.status === 'fulfilled' ? dashResult.value : null;
      const libRes=libResult.status === 'fulfilled' ? libResult.value : [];
      const studRes=studResult.status === 'fulfilled' ? studResult.value : [];
      const seatsRes=seatsResult.status === 'fulfilled' ? seatsResult.value : null;
      const statsRes=statsResult.status === 'fulfilled' ? statsResult.value : null;
      results.slice(1).forEach((result, index) => {
        if (result.status === 'rejected') console.warn(`Payment page supplementary request ${index + 1} failed:`, result.reason);
      });
      const dashData=dashRes?.data||dashRes||null;
      const dashboardRoot=dashData?.dashboard||dashData;
      setPayments(payData); setDashboard(dashboardRoot);
      setRevenueStats(statsRes?.data||statsRes||null);
      const dueMap={};
      (dashboardRoot?.students_with_due||[]).forEach(student=>{
        const sid=student.studentid||student.student_id||student.studentId||student.id||student._id;
        if(sid) dueMap[String(sid)]=Number(student.amount_due||student.total_amount_due||student.due||0);
      });
      setOutstandingMap(dueMap);
      const libData=extractList(libRes, ['libraries']); setLibraries(libData);
      const studData=extractList(studRes, ['students']); if(studData.length) setStudents(studData);
      if(seatsRes && libData.length){
        const libMap={}; libData.forEach(l=>[l.id,l.library_id,l._id].filter(Boolean).forEach(id=>{libMap[String(id)]=l.library_name||l.name;}));
        const roomLibMap={}; extractList(seatsRes, ['rooms']).forEach(r=>{ const rId=r.room_id||r.roomid||r.id; if(r.library_id&&rId) roomLibMap[String(rId)]=libMap[String(r.library_id)]; });
        const sLibMap={}; extractList(seatsRes, ['seats']).forEach(seat=>{ const seatId=seat.seat_id||seat.seatid||seat.id; const roomId=seat.room_id||seat.roomid; if(seatId&&roomId&&roomLibMap[String(roomId)]) sLibMap[String(seatId)]=roomLibMap[String(roomId)]; });
        setSeatLibraryMap(sLibMap);
      }
    } catch(e){
      if(requestId===paymentRequestId.current){
        console.error('Failed to load payments:', e);
        toast?.(e?.response?.data?.error || e?.error || 'Failed to load payments.');
      }
    } finally{
      if(requestId===paymentRequestId.current)setGenerating(false);
    }
  }, [filters, filter, searchQuery, toast]);

  useEffect(()=>{
    if (initialPaymentsLoaded.current) return;
    initialPaymentsLoaded.current = true;
    fetchLive();
  },[fetchLive]);

  // Refetch when filters are restored from the URL (e.g. browser back/forward).
  useEffect(()=>{
    const key=JSON.stringify([filters,filter,searchQuery]);
    if (key===lastFetchKey.current) return;
    fetchLive(filters,filter,searchQuery);
  },[filters,filter,searchQuery,fetchLive]);

  // Listen for data update events from other components
  useEffect(() => {
    const handleStudentUpdate = () => {
      console.log('🔄 Payments: Student data updated, refreshing...');
      fetchLive(filters, filter, searchQuery);
    };

    const handlePaymentUpdate = () => {
      console.log('🔄 Payments: Payment data updated, refreshing...');
      fetchLive(filters, filter, searchQuery);
    };

    window.addEventListener('studentDataUpdated', handleStudentUpdate);
    window.addEventListener('paymentDataUpdated', handlePaymentUpdate);

    return () => {
      window.removeEventListener('studentDataUpdated', handleStudentUpdate);
      window.removeEventListener('paymentDataUpdated', handlePaymentUpdate);
    };
  }, [filters, filter, searchQuery, fetchLive]);

  const libraryMap=useMemo(()=>{ const m={}; libraries.forEach(l=>{ const n=l.library_name||l.name; [l.library_id,l.id,l._id].filter(Boolean).forEach(id=>{m[id]=n; m[String(id)]=n;}); }); return m; },[libraries]);
  const getLibraryName=useCallback((p)=>{
    if(p.library_name || p.libraryName) return p.library_name || p.libraryName;
    if(seatLibraryMap && students.length){ const sid=p.student_id||p.studentid||p.studentId; const st=students.find(s=>String(s.id||s.student_id||s.studentid||s.studentId||s._id)===String(sid)); if(st && seatLibraryMap[String(st.seatid||st.seat_id||st.seatId)]) return seatLibraryMap[String(st.seatid||st.seat_id||st.seatId)]; }
    const ids=[p.library_id,p.libraryId,p.library].filter(Boolean);
    for(let id of ids){ const sId=String(typeof id==='object'? id.library_id||id.libraryId||id._id||id.id||'':id); if(libraryMap[id]) return libraryMap[id]; if(libraryMap[sId]) return libraryMap[sId]; }
    return 'Unknown Library';
  }, [seatLibraryMap, students, libraryMap]);

  const filteredStudents=useMemo(()=>{
    const base=students.slice(0,100); const q=(studentSearch||searchQuery).toLowerCase();
    if(!q) return base;
    return students.filter(s=> (s.name||s.student_name||s.studentname||'').toLowerCase().includes(q) || (s.email||'').toLowerCase().includes(q) || String(s.phone||s.contact_no||'').includes(q)).slice(0,50);
  },[students, studentSearch, searchQuery]);

  const pendingStudents=useMemo(()=>{
    const query=searchQuery.trim().toLowerCase();
    return students.filter(student=>{
      const sid=student.id||student.student_id||student.studentid||student.studentId||student._id;
      const dueAmount=Number(outstandingMap[String(sid)]||student.amount_due||student.total_amount_due||0);
      const subEnd=student.subscription_end||student.subscriptionend||student.subscriptionEnd||student.subscription_end_date;
      const endDate=subEnd? new Date(subEnd):null;
      const validEnd=endDate && !Number.isNaN(endDate.getTime());
      const isExpired=validEnd && endDate < new Date();
      const isExpiringSoon=validEnd && (endDate-new Date())/(1000*60*60*24) <= 7;
      if(query && !(student.name||'').toLowerCase().includes(query) && !(student.email||'').toLowerCase().includes(query)) return false;
      if(filters.libraryId){
        const libName=libraryMap[filters.libraryId]||filters.library;
        const seatLib=seatLibraryMap[String(student.seatid||student.seat_id||student.seatId)];
        const studentLib=student.library_name_display||student.library_name||student.libraryName||student.library;
        if(String(student.library_id||student.libraryId||'')!==String(filters.libraryId) && seatLib!==libName && studentLib!==libName) return false;
      }
      if(filters.studentId && String(sid)!==String(filters.studentId)) return false;
      return dueAmount>0 || isExpired || isExpiringSoon;
    });
  },[students,outstandingMap,searchQuery,filters.libraryId,filters.library,filters.studentId,libraryMap,seatLibraryMap]);

  const handleStudentChange=useCallback(async(e)=>{
    const studentId=e.target.value;
    const requestId=++studentRequestId.current;
    setStudentLoading(Boolean(studentId));
    setFormData(prev=>({...prev, studentid:studentId, planid:'', amount:'', receivedAmount:'', paymentType:'subscription', upgradeAmount:'', subscriptionAmount:'', payBoth:false, renewalRequested:false}));
    setStudentPlans([]); setSelectedStudent(null); setOutstandingInfo(null); setSeatUpgrade(null); setSelectedPlanAmount(null);
    if(!studentId) return;
    const student=students.find(s=> String(s.id||s.student_id||s.studentid||s.studentId||s._id)===String(studentId));
    setSelectedStudent(student);
    if(!student){setStudentLoading(false);return;}
    try{
      const [plansResult, outInfo, upgradeResult] = await Promise.all([
        getStudentPaymentPlans(studentId), getStudentOutstanding(studentId),
        axios.get(`/api/students/${studentId}/seat-upgrade`).then(r=>r.data||null).catch(()=>null)
      ]);
      const plans=extractList(plansResult,['plans','payment_plans','paymentPlans']);
      const outstanding=outInfo?.data||outInfo;
      const upgradeData=upgradeResult?.data||upgradeResult?.upgrade||upgradeResult;
      const studentCategory=String(student.seat_category||student.seatcategory||student.seatCategory||student.category||student.plan_type||'').toLowerCase().replace(/[^a-z]/g,'');
      const desiredCategory=studentCategory.includes('reserved')?'reserved':studentCategory.includes('non')?'non_ac':studentCategory.includes('ac')?'ac':'';
      const matchingPlans=desiredCategory?plans.filter(plan=>{
        const category=String(plan.seat_category||plan.seatcategory||plan.seatCategory||plan.category||'').toLowerCase().replace(/[^a-z]/g,'');
        return desiredCategory==='reserved'?category.includes('reserved'):desiredCategory==='non_ac'?category.includes('non'):category.includes('ac')&&!category.includes('non');
      }):[];
      const availablePlans=matchingPlans.length?matchingPlans:plans;
      setStudentPlans(availablePlans); setOutstandingInfo(outstanding);
      const upgradeId=upgradeData?.upgrade_id||upgradeData?.seat_upgrade_id||upgradeData?.id;
      const activeUpgrade=(upgradeId && String(upgradeData?.status||'').toLowerCase()==='pending')?{
        ...upgradeData,
        upgrade_id:upgradeId,
        amount_due:upgradeData.amount_due??upgradeData.amountDue??upgradeData.due_amount,
        fee_amount:upgradeData.fee_amount??upgradeData.feeAmount
      }:null;
      setSeatUpgrade(activeUpgrade);
      const subscriptionEnd=student.subscription_end||student.subscriptionend||student.subscriptionEnd||student.subscription_end_date;
      const endDate=subscriptionEnd?new Date(subscriptionEnd):null;
      const daysRemaining=endDate&&!Number.isNaN(endDate.getTime())?(endDate-new Date())/(1000*60*60*24):Infinity;
      const renewalRequested=Boolean(student.renewal_requested||student.renewalRequested)||daysRemaining<=5;
      setFormData(prev=>({...prev,renewalRequested}));
      const planAmount=Number(outstanding?.plan_amount??outstanding?.planAmount);
      if(planAmount>0){ const due=Number(outstanding.amount_due||outstanding.amountDue||0); const def=due>0?String(due):String(planAmount); setSelectedPlanAmount(planAmount); setFormData(prev=>({...prev,receivedAmount:def,amount:def})); }
      if(availablePlans.length>0 && !activeUpgrade){ let selected=availablePlans[0]; if(planAmount>0){ const match=availablePlans.find(p=> Math.abs(Number(p.amount||0)-planAmount)<0.01); if(match) selected=match; } const amount=Number(selected.amount||0); setFormData(prev=>({...prev, planid:String(selected.plan_id||selected.planid||selected.planId), amount:String(amount), receivedAmount:String(Number(outstanding?.amount_due||outstanding?.amountDue)||amount)})); setSelectedPlanAmount(amount); }
      if(activeUpgrade?.upgrade_id){ const due=Number(activeUpgrade.amount_due||0); setFormData(prev=>({...prev, planid:'', receivedAmount:due>0?String(due):'', amount:due>0?String(due):'', paymentType:'upgrade', upgradeAmount:String(due)})); setSelectedPlanAmount(activeUpgrade.fee_amount||due); }
    }catch(err){
      if(requestId===studentRequestId.current){
        console.error('Failed to load student payment details:', err);
        setStudentPlans([]);
        toast?.(err?.response?.data?.error || "Could not load this student's plans and balance.");
      }
    } finally{
      if(requestId===studentRequestId.current) setStudentLoading(false);
    }
  },[students, toast]);

  useEffect(() => {
    if (!initialStudentId || !students.length) return;
    if (String(handledInitialStudent.current) === String(initialStudentId)) return;
    const student = students.find(s => String(s.id || s.student_id || s.studentid || s.studentId || s._id) === String(initialStudentId));
    if (!student) return;
    handledInitialStudent.current = initialStudentId;
    setShowAddModal(true);
    handleStudentChange({ target: { value: String(initialStudentId) } });
  }, [initialStudentId, students, handleStudentChange]);

  const handlePlanSelect=(e)=>{
    const planId=e.target.value; const plan=studentPlans.find(p=> String(p.planid||p.plan_id||p.planId)===String(planId));
    setFormData(prev=>({...prev, planid:planId||'', amount:plan?String(plan.amount||0):'', receivedAmount:plan?String(plan.amount||0):prev.receivedAmount, paymentType:'subscription', payBoth:false}));
    setSelectedPlanAmount(plan? (plan.amount||0):null);
  };

  const selectPaymentMode=(mode)=>{
    if(!seatUpgrade) return;
    const subscriptionDue=Number(outstandingInfo?.subscription_amount_due ?? outstandingInfo?.subscriptionAmountDue ?? outstandingInfo?.amount_due ?? 0);
    const upgradeDue=Number(seatUpgrade.amount_due||seatUpgrade.amountDue||0);
    if(mode==='both'){
      setFormData(prev=>({...prev, paymentType:'subscription', payBoth:true, upgradeAmount:String(upgradeDue), subscriptionAmount:String(subscriptionDue), receivedAmount:String(subscriptionDue+upgradeDue)}));
      setSelectedPlanAmount(subscriptionDue+upgradeDue);
    } else if(mode==='upgrade'){
      setFormData(prev=>({...prev, paymentType:'upgrade', payBoth:false, upgradeAmount:String(upgradeDue), receivedAmount:String(upgradeDue)}));
      setSelectedPlanAmount(Number(seatUpgrade.fee_amount||upgradeDue));
    } else {
      setFormData(prev=>({...prev, paymentType:'subscription', payBoth:false, subscriptionAmount:String(subscriptionDue), receivedAmount:String(subscriptionDue)}));
      setSelectedPlanAmount(subscriptionDue);
    }
  };

  const handleSubmitPayment=async(e)=>{
    e.preventDefault(); setErrors({});
    if(!formData.studentid){ setErrors({student:'Please select a student'}); return; }
    const receivedAmount=Number(formData.receivedAmount);
    if(!Number.isFinite(receivedAmount) || receivedAmount<=0){ setErrors({amount:'Enter a valid amount greater than zero'}); return; }
    if (savingPayment) return;
    setSavingPayment(true);

    // Add unique submission ID to prevent duplicate submissions
    const paymentSubmissionKey = `payment-submit-${Date.now()}`;
    try{
      if(seatUpgrade?.upgrade_id && formData.paymentType==='upgrade'){
        await axios.post(`/api/students/${formData.studentid}/seat-upgrade/${seatUpgrade.upgrade_id}/payment`,{ amount:receivedAmount, payment_method:formData.paymentmethod, transaction_id:formData.transaction_id||null });
        toast?.('Seat Upgrade Paid');
      } else {
        const studentId=Number.isSafeInteger(Number(formData.studentid)) ? Number(formData.studentid) : formData.studentid;
        const planId=Number.isSafeInteger(Number(formData.planid)) ? Number(formData.planid) : formData.planid;
        await createPayment({ studentid:studentId, planid:formData.planid? planId:null, amount:receivedAmount, received_amount:receivedAmount, paymentmethod:formData.paymentmethod, transactionid:formData.transaction_id||null, send_reminder:Boolean(formData.sendReminder), renewal_requested:Boolean(formData.renewalRequested), pay_both:Boolean(formData.payBoth) });
        toast?.('Payment recorded');
      }
      // Close modal and reset form BEFORE dispatching event
      setShowAddModal(false); setFormData({studentid:'',planid:'',amount:'',receivedAmount:'',paymentmethod:'cash',transaction_id:'',status:'completed',sendReminder:false,renewalRequested:false,paymentType:'subscription',upgradeAmount:'',subscriptionAmount:'',payBoth:false});
      setSelectedStudent(null); setStudentPlans([]); setOutstandingInfo(null); setSeatUpgrade(null); setSelectedPlanAmount(null);

      // Dispatch event AFTER modal is closed to prevent duplicate submissions
      setTimeout(() => {
        window.dispatchEvent(new CustomEvent('paymentDataUpdated', {
          detail: { studentId: formData.studentid, type: seatUpgrade ? 'seat-upgrade-payment' : 'subscription-payment', timestamp: paymentSubmissionKey }
        }));
      }, 100);

      await fetchLive(filters, filter, searchQuery);
    }catch(error){ console.error('Failed to record payment:', error); toast?.(error.response?.data?.error || error.error || 'Failed to record payment.'); setSavingPayment(false); } finally{
      // Ensure savingPayment is set to false with a small delay
      setTimeout(() => setSavingPayment(false), 300);
    }
  };

  const formatPlanOption=(plan)=>{ const name=plan.plan_name||plan.planname||'Plan'; const amt=plan.amount||0; const days=plan.duration_days||plan.durationdays||0; const cat=plan.seat_category||plan.seatcategory||''; return `${name} (${cat.toUpperCase()}) - ${currency(amt)} (${days} days)`; };
  const filtered=useMemo(()=>{
    let list=payments;
    const normalizeStatus = payment => String(payment.status || payment.payment_status || payment.paymentStatus || 'completed').toLowerCase();
    const paymentStudentId = payment => payment.student_id || payment.studentid || payment.studentId;
    const paymentLibraryId = payment => {
      const library = payment.library_id || payment.libraryId || payment.library;
      return library && typeof library === 'object' ? library.library_id || library.id || library._id : library;
    };
    if(filter==='today'||filter==='this-month'){
      const now=new Date();
      list=list.filter(payment=>{
        const date=new Date(paymentDate(payment));
        if(Number.isNaN(date.getTime())) return false;
        return filter==='today'
          ? date.toDateString()===now.toDateString()
          : date.getMonth()===now.getMonth() && date.getFullYear()===now.getFullYear();
      });
    }
    if(filters.start){
      const start=new Date(`${filters.start}T00:00:00`);
      list=list.filter(payment=>{ const date=new Date(paymentDate(payment)); return !Number.isNaN(date.getTime()) && date>=start; });
    }
    if(filters.end){
      const end=new Date(`${filters.end}T23:59:59.999`);
      list=list.filter(payment=>{ const date=new Date(paymentDate(payment)); return !Number.isNaN(date.getTime()) && date<=end; });
    }
    if(filters.libraryId){
      list=list.filter(payment=>String(paymentLibraryId(payment) || '')===String(filters.libraryId) || getLibraryName(payment)===filters.library);
    }
    if(filters.studentId){
      list=list.filter(payment=>String(paymentStudentId(payment) || '')===String(filters.studentId));
    }
    if(filters.status && filters.status!=='All'){
      const expected=filters.status.toLowerCase();
      list=list.filter(payment=>{
        const status=normalizeStatus(payment);
        if(expected==='completed') return ['completed','success','successful','paid'].includes(status);
        return status===expected;
      });
    }
    if(searchQuery){
      const q=searchQuery.trim().toLowerCase();
      list=list.filter(p=> [p.student_name,p.studentname,p.studentName,p.transaction_id,p.transactionid,p.transactionId,p.plan,p.plan_name,p.planname].some(value=>String(value||'').toLowerCase().includes(q)) || getLibraryName(p).toLowerCase().includes(q));
    }
    return list;
  },[payments,searchQuery,filter,filters.start,filters.end,filters.libraryId,filters.library,filters.studentId,filters.status,getLibraryName]);
  const listCount=filter==='pending'? pendingStudents.length:filtered.length;
  const totalPages=Math.ceil(listCount/rowsPerPage);
  const pageStart=(currentPage-1)*rowsPerPage;
  const currentItems=filter==='pending'? pendingStudents.slice(pageStart,pageStart+rowsPerPage) : filtered.slice(pageStart,pageStart+rowsPerPage);
  const visiblePageCount=Math.min(totalPages,5);
  const firstVisiblePage=Math.max(1,Math.min(currentPage-2,totalPages-visiblePageCount+1));
  const visiblePages=Array.from({length:visiblePageCount},(_,index)=>firstVisiblePage+index);
  const rows=(filter==='pending'?[]:currentItems).map(p=>{
    const id=p.payment_id||p.paymentid||p.id||p._id;
    const rawStatus=String(p.status||p.payment_status||p.paymentStatus||'completed').toLowerCase();
    const status=['completed','success','successful','paid'].includes(rawStatus)?'paid':rawStatus;
    return {
      payment:p,
      values:[
        id||`PAY-${id}`,
        p.student_name||p.studentname||p.studentName||'Unknown',
        getLibraryName(p),
        p.plan||p.plan_name||p.planname||'Monthly',
        currency(Number(p.amount||p.received_amount||p.receivedAmount||0)),
        formatPaymentDate(paymentDate(p)),
        p.method||p.payment_method||p.paymentmethod||'UPI',
        status.charAt(0).toUpperCase()+status.slice(1)
      ]
    };
  });
  const getReceiptId=payment=>payment?.payment_id||payment?.paymentid||payment?.paymentId||payment?.id||payment?._id;
  const handleReceipt=async payment=>{
    const paymentId=getReceiptId(payment);
    if(!paymentId){toast?.('Payment ID is missing');return;}
    setDownloadingReceiptId(paymentId);
    try{
      const pdf=await downloadPaymentReceiptPdf(paymentId);
      const url=URL.createObjectURL(pdf);
      const anchor=document.createElement('a');
      anchor.href=url;
      anchor.download=`receipt-${paymentId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(()=>URL.revokeObjectURL(url),1000);
    }catch(error){
      console.error('Failed to download payment receipt:',error);
      toast?.(error?.message||'Could not download receipt.');
    }finally{setDownloadingReceiptId(null);}
  };
  const handleCombinedReceipt=async payment=>{
    const paymentId=getReceiptId(payment);
    if(!paymentId){toast?.('Payment ID is missing');return;}
    setDownloadingReceiptId(paymentId);
    try{
      const {blob,filename}=await downloadCombinedPaymentReceiptPdf(paymentId);
      const url=URL.createObjectURL(blob);
      const anchor=document.createElement('a');
      anchor.href=url;
      anchor.download=filename||`combined-receipt-${paymentId}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(()=>URL.revokeObjectURL(url),1000);
      toast?.('Combined receipt downloaded.');
    }catch(error){
      console.error('Failed to download combined payment receipt:',error);
      toast?.(error?.message||'Could not download combined receipt.');
    }finally{setDownloadingReceiptId(null);}
  };
  const findPaymentStudent=payment=>{
    const studentId=payment?.student_id||payment?.studentid||payment?.studentId;
    return payment?.student || students.find(student=>String(student.id||student.student_id||student.studentid||student.studentId||student._id)===String(studentId));
  };
  const readBlobAsDataUrl=blob=>new Promise((resolve,reject)=>{
    const reader=new FileReader();
    reader.onload=()=>typeof reader.result==='string'?resolve(reader.result):reject(new Error('Could not read the receipt file.'));
    reader.onerror=()=>reject(reader.error||new Error('Could not read the receipt file.'));
    reader.readAsDataURL(blob);
  });
  const handleViewCombinedReceipt=async payment=>{
    const paymentId=getReceiptId(payment);
    if(!paymentId){toast?.('Payment ID is missing');return;}
    const receiptWindow=window.open('about:blank','_blank');
    if(!receiptWindow){toast?.('Allow pop-ups to view the receipt.');return;}
    receiptWindow.document.title='Preparing receipt';
    receiptWindow.document.body.textContent='Preparing combined payment receipt...';
    setDownloadingReceiptId(paymentId);
    try{
      const {blob}=await downloadCombinedPaymentReceiptPdf(paymentId);
      const url=URL.createObjectURL(blob);
      receiptWindow.location.href=url;
      window.setTimeout(()=>URL.revokeObjectURL(url),60000);
    }catch(error){
      receiptWindow.close();
      console.error('Failed to prepare combined payment receipt:',error);
      toast?.(error?.message||'Could not open combined receipt.');
    }finally{setDownloadingReceiptId(null);}
  };
  const handleWhatsAppReceipt=async payment=>{
    const paymentId=getReceiptId(payment);
    const student=findPaymentStudent(payment);
    const phone=student?.phone||student?.contact_no||student?.contactNo||student?.mobile||payment.phone||payment.student_phone;
    const studentName=payment.student_name||payment.studentname||payment.studentName||student?.name||student?.student_name||'Student';
    if(!paymentId){toast?.('Payment ID is missing');return;}
    if(!phone){toast?.('A phone number is required to send the receipt.');return;}
    setDownloadingReceiptId(paymentId);
    try{
      const {blob,filename}=await downloadCombinedPaymentReceiptPdf(paymentId);
      const documentName=filename||`Combined_Receipt_${studentName.replace(/[^a-z0-9_-]/gi,'_')}.pdf`;
      await sendPaymentReceiptPdfWhatsApp(phone,studentName,paymentId,await readBlobAsDataUrl(blob),documentName);
      toast?.('Combined receipt sent through WhatsApp.');
    }catch(error){
      console.error('Failed to send combined receipt through WhatsApp:',error);
      toast?.(error?.error||error?.message||'Could not send receipt through WhatsApp.');
    }finally{setDownloadingReceiptId(null);}
  };
  const handleWhatsAppPaymentHistory=async student=>{
    const studentId=student.id||student.student_id||student.studentid||student.studentId||student._id;
    const studentName=student.name||student.student_name||student.studentname||'Student';
    const phone=student.phone||student.contact_no||student.contactNo||student.mobile;
    if(!studentId){toast?.('Student ID is missing.');return;}
    if(!phone){toast?.('A phone number is required to send payment history.');return;}
    setSendingHistoryId(studentId);
    try{
      const historyResult=await getPayments({student_id:studentId},true);
      const studentPayments=extractList(historyResult,['payments']).filter(payment=>String(payment.student_id||payment.studentid||payment.studentId)===String(studentId));
      const totalPaid=studentPayments.filter(payment=>['completed','success','successful','paid'].includes(String(payment.status||payment.payment_status||payment.paymentStatus||'completed').toLowerCase()))
        .reduce((total,payment)=>total+Number(payment.amount||payment.received_amount||payment.receivedAmount||0),0);
      const paymentHistory=studentPayments.map(payment=>({
        paymentDate:paymentDate(payment)||payment.created_at||'',
        receiptNumber:payment.receipt_number||payment.receiptNumber||payment.payment_id||payment.paymentid||payment.id||'',
        paymentMethod:payment.payment_method||payment.paymentmethod||payment.method||'',
        transactionId:payment.transaction_id||payment.transactionid||payment.transactionId||'',
        status:payment.status||payment.payment_status||'completed',
        amount:Number(payment.amount||payment.received_amount||payment.receivedAmount||0)
      })).sort((a,b)=>new Date(b.paymentDate).getTime()-new Date(a.paymentDate).getTime());
      const dueAmount=Number(outstandingMap[String(studentId)]||student.amount_due||student.total_amount_due||0);
      await sendStudentPaymentHistoryWhatsApp(phone,studentName,paymentHistory,totalPaid,dueAmount);
      toast?.('Payment history sent through WhatsApp.');
    }catch(error){
      console.error('Failed to send student payment history through WhatsApp:',error);
      toast?.(error?.error||error?.message||'Could not send payment history.');
    }finally{
      setSendingHistoryId(null);
    }
  };
  const clearFilters=()=>{ const cleared={search:'',status:'All',library:'All',libraryId:'',studentId:'',studentName:'',start:'',end:''}; setFilters(cleared); setFilter('all'); setSearchQuery(''); setCurrentPage(1); fetchLive(cleared,'all',''); };
  const openAddPayment=()=>{
    studentRequestId.current+=1;
    setStudentLoading(false);
    setFormData({studentid:'',planid:'',amount:'',receivedAmount:'',paymentmethod:'cash',transaction_id:'',status:'completed',sendReminder:false,renewalRequested:false,paymentType:'subscription',upgradeAmount:'',subscriptionAmount:'',payBoth:false});
    setErrors({});
    setSelectedStudent(null);
    setStudentPlans([]);
    setOutstandingInfo(null);
    setSeatUpgrade(null);
    setSelectedPlanAmount(null);
    setStudentSearch('');
    setShowAddModal(true);
  };

  const getUpiQrUrl = () => {
    try {
      const tenantStr = localStorage.getItem('tenantinfo');
      const tenant = tenantStr ? JSON.parse(tenantStr) : {};
      const qr = tenant.upi_qr_code || localStorage.getItem('payment_qr') || '';
      if (!qr) return '';
      return qr.startsWith('http') || qr.startsWith('data:') || qr.startsWith('blob:') ? qr : `${axios.defaults.baseURL || ''}${qr}`;
    } catch { return ''; }
  };

  const collectedPayments=payments.filter(p=>String(p.status||p.payment_status||'').toLowerCase()!=='failed');
  const todayKey=dateKey(new Date());
  const monthKey=todayKey.slice(0,7);
  const paymentAmount=payment=>Number(payment.amount||payment.received_amount||payment.receivedAmount||0);
  const fallbackTotal=collectedPayments.reduce((sum,p)=>sum+paymentAmount(p),0);
  const fallbackToday=collectedPayments.filter(p=>dateKey(paymentDate(p))===todayKey).reduce((sum,p)=>sum+paymentAmount(p),0);
  const fallbackMonth=collectedPayments.filter(p=>dateKey(paymentDate(p)).slice(0,7)===monthKey).reduce((sum,p)=>sum+paymentAmount(p),0);
  const stats={
    total:Number(revenueStats?.totalcollection ?? revenueStats?.total_collection ?? revenueStats?.total_revenue ?? dashboard?.total_revenue ?? fallbackTotal),
    today:Number(revenueStats?.todayrevenue ?? revenueStats?.today_revenue ?? fallbackToday),
    month:Number(revenueStats?.monthlyrevenue ?? revenueStats?.monthly_revenue ?? fallbackMonth),
    count:Number(revenueStats?.totalpayments ?? revenueStats?.total_payments ?? dashboard?.total_payments ?? payments.length)
  };

  return (
    <div className="payments-page">
      <header className="dash-header payments-page-header">
        <div className="dash-header-bar">
          <div className="payments-header-left">
            <div className="payments-heading-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none"><path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H20v14H6.5A2.5 2.5 0 0 1 4 16.5v-9Zm0 0A2.5 2.5 0 0 1 6.5 10H20M8 14h5m-5 3h8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M16 2v6m-3-3h6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
            </div>
            <div className="payments-header-info">
              <div className="payments-heading-text">
                <h1 className="dash-header-title">Payments</h1>
                <p className="dash-header-subtitle">Track collections, review payment activity, and record payments.</p>
              </div>
              <div className="payments-heading-actions dash-header-actions">
          <button className={`payments-refresh-btn${generating ? ' is-refreshing' : ''}`} type="button" onClick={()=>{ const cleared={...filters, studentId:'', studentName:''}; setFilters(cleared); fetchLive(cleared,filter,searchQuery); }} disabled={generating} aria-label="Refresh payments" title="Refresh payments">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 7v5h-5M4 17v-5h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M5.5 9a7 7 0 0 1 11.6-2L20 12M4 12l2.9 5a7 7 0 0 0 11.6-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
            {generating ? 'Refreshing…' : 'Refresh'}
          </button>
          <button className="add-payment-btn dash-action-btn primary" type="button" onClick={openAddPayment}>
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 5v14m-7-7h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
            Add Payment
          </button>
              </div>
            </div>
          </div>
        </div>
      </header>
      <section className={`stats-group${canViewFinancials ? '' : ' stats-group-limited'}`} aria-label="Payment summary">
        {canViewFinancials && <article className="stat-card">
          <div className="stat-card-top"><span>Total Revenue</span><i className="payments-stat-icon revenue"><svg viewBox="0 0 24 24" role="img" aria-label="Rupee"><text x="12" y="18" textAnchor="middle" fontFamily="Arial, sans-serif" fontSize="19" fontWeight="700" fill="currentColor">₹</text></svg></i></div>
          <b className="stat-card-value">{currency(stats.total)}</b><small className="stat-card-caption">All collected payments</small>
        </article>}
        <article className="stat-card">
          <div className="stat-card-top"><span>Today's Collection</span><i className="payments-stat-icon today"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.7"/><path d="M16 3v4M8 3v4M3 10h18m-13 4h3m-3 3h7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg></i></div>
          <b className="stat-card-value">{currency(stats.today)}</b><small className="stat-card-caption">{new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</small>
        </article>
        <article className="stat-card">
          <div className="stat-card-top"><span>This Month</span><i className="payments-stat-icon month"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 19V5m0 14h17M7 15l4-4 3 2 6-7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/><path d="M16 6h4v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/></svg></i></div>
          <b className="stat-card-value">{currency(stats.month)}</b><small className="stat-card-caption">{new Date().toLocaleDateString('en-IN',{month:'long',year:'numeric'})}</small>
        </article>
        <article className="stat-card">
          <div className="stat-card-top"><span>Total Payments</span><i className="payments-stat-icon count"><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2Zm2 5h10m-10 4h4m-4 4h7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg></i></div>
          <b className="stat-card-value">{stats.count}</b><small className="stat-card-caption">{payments.length} loaded records</small>
        </article>
      </section>

      {showPaymentFilters ? <section id="payments-filter-panel" className="filter-card payments-filter-card" aria-label="Payment filters">
        <header className="payments-filter-heading">
          <div><h2>Filter payments</h2><p>Search and narrow payment records by date, student, library, or status.</p></div>
          <div className="payments-filter-heading-actions">
            <button type="button" className="clear-btn" onClick={clearFilters}>Clear filters</button>
            <button type="button" className="filter-toggle-btn" aria-expanded={showPaymentFilters} aria-controls="payments-filter-panel" onClick={() => setShowPaymentFilters((visible) => !visible)}>
              <FaFilter aria-hidden="true" /><span>{showPaymentFilters ? 'Hide filters' : 'Show filters'}</span>
            </button>
          </div>
        </header>
        <div className="payments-filter-content">
        <div className="payments-filter-search">
          <input type="text" placeholder="Search student, transaction, or plan..." value={searchQuery} onChange={(e)=>{ setSearchQuery(e.target.value); setCurrentPage(1); }} className="search-input" />
        </div>
        <div className="advanced-filters payments-advanced-filters">
          <label>Library
            <UiSelect value={filters.libraryId || 'All'} onChange={e=>{ const val=e.target.value; const found=libraries.find(l=>String(l.library_id||l.id||l._id)===String(val)); const upd={...filters, library:found?(found.library_name||found.name):'All', libraryId:found?(found.library_id||found.id||found._id):''}; setFilters(upd); setCurrentPage(1); fetchLive(upd,filter,searchQuery); }}>
              <option value="All">All</option>{libraries.map(l=>{ const n=l.library_name||l.name; const id=l.library_id||l.id||l._id; return <option key={id} value={id}>{n}</option> })}
            </UiSelect>
          </label>
          <label>Student
            <div className="student-picker" onClick={()=>setShowStudentModal(true)}>{filters.studentName||'Select student'}</div>
          </label>
          <label>Start Date<input type="date" value={filters.start} onChange={e=>setFilters({...filters,start:e.target.value})} /></label>
          <label>End Date<input type="date" value={filters.end} onChange={e=>setFilters({...filters,end:e.target.value})} /></label>
          <label>Payment Status
            <UiSelect value={filters.status} onChange={e=>{setFilters({...filters,status:e.target.value});setCurrentPage(1);}}>
              <option value="All">All statuses</option><option value="completed">Completed</option><option value="pending">Pending</option><option value="failed">Failed</option>
            </UiSelect>
          </label>
          <button type="button" className="apply-btn" onClick={()=>{setCurrentPage(1);fetchLive(filters,filter,searchQuery);}} disabled={generating}>{generating?'Applying…':'Apply filters'}</button>
        </div>
        </div>
      </section> : <div className="filter-toggle-row">
        <button type="button" className="filter-toggle-btn" aria-expanded="false" aria-controls="payments-filter-panel" onClick={() => setShowPaymentFilters(true)}>
          <FaFilter aria-hidden="true" /><span>Show filters</span>
        </button>
      </div>}

      <section className="payments-quick-views-panel" aria-label="Quick payment views">
        <div className="quick-filters payments-quick-filters">
          <span className="payments-filter-section-label">Quick views</span>
          <div className="filter-chips">
            {['all','today','this-month','pending'].map(f=>(
              <button type="button" key={f} onClick={()=>{ setFilter(f); setCurrentPage(1); if(f==='all'){ const cleared={...filters, studentId:'', studentName:''}; setFilters(cleared); fetchLive(cleared,f,searchQuery); } else { fetchLive(filters,f,searchQuery); } }} className={filter===f? 'chip active':'chip'}>{f==='all'?`All (${payments.length})`: f==='pending'?`Due / expiring (${pendingStudents.length})`: f.replace('-',' ').replace(/\b\w/g,l=>l.toUpperCase())}</button>
            ))}
          </div>
        </div>
      </section>

      {filters.studentId && studentPlans.length>0 && (
        <div className="plans-panel">
          <h3>Payment Plans for {filters.studentName} - Due: {currency(outstandingInfo?.amount_due||outstandingInfo?.amountDue||0)}</h3>
          <div className="plans-grid">
            {studentPlans.map(plan=>(
              <div key={plan.plan_id||plan.planid||plan.planId} className="plan-card"><b>{plan.plan_name||plan.planname}</b><span>{currency(plan.amount)} · {plan.duration_days||plan.durationdays||plan.durationDays} days</span></div>
            ))}
          </div>
        </div>
      )}

      <div className="table-card">
        {filter==='pending' ? (
          <table className="custom-table"><thead><tr><th>Sr</th><th>Name</th><th>Library</th><th>Seat</th><th>Due Date</th><th>Amount Due</th><th>Action</th></tr></thead><tbody>{currentItems.map((s,idx)=>{ const sid=s.id||s.student_id||s.studentid||s.studentId||s._id; const due=Number(outstandingMap[String(sid)]||s.amount_due||s.total_amount_due||0); const seat=s.seat_number_display||s.seatnumber||s.seat_number||s.seat||'-'; const dueDate=s.subscription_end||s.subscriptionend||s.subscriptionEnd||s.subscription_end_date; return <tr key={sid}><td>{pageStart+idx+1}</td><td>{s.name||s.student_name||s.studentname||'Unknown'}</td><td>{s.library_name_display||s.library_name||s.libraryName||seatLibraryMap[String(s.seatid||s.seat_id||s.seatId)]||'-'}</td><td>{seat}</td><td>{dueDate? new Date(dueDate).toLocaleDateString('en-IN'):'-'}</td><td><b className={due>0?'due':''}>{due>0?currency(due):'Paid'}</b></td><td><div className="pending-row-actions"><button className="table-action-btn" onClick={()=>{ const name=s.name||s.student_name||s.studentname||'Unknown'; const upd={...filters, studentId:sid, studentName:name}; setFilters(upd); setFilter('all'); setCurrentPage(1); fetchLive(upd,'all',''); openAddPayment(); handleStudentChange({target:{value:sid}}); }}>Add Payment</button>{(s.phone||s.contact_no||s.mobile)?<button type="button" className="table-action-btn history-action" disabled={String(sendingHistoryId)===String(sid)} onClick={()=>handleWhatsAppPaymentHistory(s)}>{String(sendingHistoryId)===String(sid)?'Sending...':'History'}</button>:null}</div></td></tr>})}
          {!currentItems.length && <tr><td colSpan={7} className="empty-state">No pending students.</td></tr>}
          </tbody></table>
        ) : rows.length===0? <p className="empty">No payments found</p> : <PaymentsTable rows={rows} onReceipt={handleReceipt} onCombinedReceipt={handleCombinedReceipt} onViewCombinedReceipt={handleViewCombinedReceipt} onWhatsAppReceipt={handleWhatsAppReceipt} downloadingReceiptId={downloadingReceiptId} />}
      </div>
      <div className="payments-pagination">
        <label className="payments-page-size">Rows per page
          <UiSelect value={String(rowsPerPage)} onChange={event=>{setRowsPerPage(Number(event.target.value));setCurrentPage(1);}}>
            {[5,10,25,50,100].map(size=><option key={size} value={size}>{size}</option>)}
          </UiSelect>
        </label>
        <span className="payments-page-range">{listCount ? `${pageStart+1}-${Math.min(pageStart+rowsPerPage,listCount)} of ${listCount} ${filter==='pending'?'students':'payments'}` : `0 ${filter==='pending'?'students':'payments'}`}</span>
        <nav className="payments-page-buttons" aria-label="Payments table pages">
          <button type="button" className="payments-page-arrow" onClick={()=>setCurrentPage(page=>Math.max(1,page-1))} disabled={currentPage<=1} aria-label="Previous page">‹</button>
          {visiblePages.map(page=><button type="button" key={page} className={page===currentPage?'active':''} onClick={()=>setCurrentPage(page)} aria-current={page===currentPage?'page':undefined}>{page}</button>)}
          <button type="button" className="payments-page-arrow" onClick={()=>setCurrentPage(page=>Math.min(totalPages,page+1))} disabled={!totalPages || currentPage>=totalPages} aria-label="Next page">›</button>
        </nav>
      </div>

      {/* SINGLE ADD PAYMENT MODAL - UPI QR FETCHED */}
      {showAddModal && (
        <div className="modal-backdrop" onMouseDown={e=> e.target===e.currentTarget && setShowAddModal(false)}>
          <section className="payment-modal payment-modal-wide">
            <div className="payment-modal-header"><h2>Record New Payment</h2><button type="button" aria-label="Close payment form" onClick={()=>!savingPayment&&setShowAddModal(false)}>{'\u00D7'}</button></div>
            <form onSubmit={handleSubmitPayment} className="payment-form">
              <div className="form-grid-2">
                <label>Student *<div className="student-select-row"><input readOnly placeholder="Click 'Select' to choose..." value={studentLoading?'Loading student details...':selectedStudent? selectedStudent.name||selectedStudent.student_name||selectedStudent.studentname:''} /><button type="button" onClick={()=>setShowStudentModal(true)}>Select</button></div>{errors.student && <span className="error">{errors.student}</span>}</label>
                <label>Plan<UiSelect value={formData.planid} onChange={handlePlanSelect} disabled={studentLoading}><option value="">{studentLoading?'Loading plans...':seatUpgrade? 'Upgrade payment':'Select student first'}</option>{studentPlans.map(p=> <option key={p.plan_id||p.planid||p.planId} value={p.plan_id||p.planid||p.planId}>{formatPlanOption(p)}</option>)}</UiSelect></label>
              </div>
              <div className="form-grid-2">
                <label>Plan Amount<input value={selectedPlanAmount != null ? currency(selectedPlanAmount) : 'Auto-fill when plan is selected'} readOnly className="readonly" />{outstandingInfo && <span className="due-text">Due: {currency(outstandingInfo.amount_due)} | Plan: {currency(outstandingInfo.plan_amount || selectedPlanAmount)}</span>}</label>
                <label>Amount Received *<input type="number" step="0.01" value={formData.receivedAmount} onChange={e=> setFormData({...formData, receivedAmount:e.target.value})} placeholder="Enter received amount" />{errors.amount && <span className="error">{errors.amount}</span>}</label>
              </div>
              {seatUpgrade && (
                <div className="upgrade-box">
                  <b>Reserved Seat Upgrade Detected</b>
                  <div className="upgrade-summary">Fee {currency(seatUpgrade.fee_amount || seatUpgrade.amount_due)} | Upgrade due {currency(seatUpgrade.amount_due)} | Subscription due {currency(outstandingInfo?.subscription_amount_due ?? outstandingInfo?.subscriptionAmountDue ?? outstandingInfo?.amount_due ?? outstandingInfo?.amountDue)}</div>
                  <div className="upgrade-mode-grid">
                    <button type="button" className={formData.paymentType === 'subscription' && !formData.payBoth ? 'active' : ''} onClick={() => selectPaymentMode('subscription')}><b>Pay Subscription</b><small>{currency(outstandingInfo?.subscription_amount_due ?? outstandingInfo?.subscriptionAmountDue ?? outstandingInfo?.amount_due ?? outstandingInfo?.amountDue)}</small></button>
                    <button type="button" className={formData.paymentType === 'upgrade' ? 'active' : ''} onClick={() => selectPaymentMode('upgrade')}><b>Pay Upgrade</b><small>{currency(seatUpgrade.amount_due)}</small></button>
                    <button type="button" className={formData.payBoth ? 'active' : ''} onClick={() => selectPaymentMode('both')}><b>Pay Both</b><small>{currency(Number(outstandingInfo?.subscription_amount_due ?? outstandingInfo?.subscriptionAmountDue ?? outstandingInfo?.amount_due ?? outstandingInfo?.amountDue ?? 0) + Number(seatUpgrade.amount_due || 0))}</small></button>
                  </div>
                  {formData.payBoth && <div className="upgrade-split">Total: {currency(formData.receivedAmount)} = Subscription {currency(outstandingInfo?.subscription_amount_due ?? outstandingInfo?.subscriptionAmountDue ?? outstandingInfo?.amount_due ?? outstandingInfo?.amountDue)} + Upgrade {currency(seatUpgrade.amount_due)}</div>}
                </div>
              )}
              <div className="form-grid-2">
                <label className="checkbox-label"><input type="checkbox" checked={formData.sendReminder} onChange={e=>setFormData({...formData, sendReminder:e.target.checked})} /> Send payment reminder if amount due</label>
                <label>Payment Method *<UiSelect value={formData.paymentmethod} onChange={e=>setFormData({...formData, paymentmethod:e.target.value})}><option value="cash">Cash</option><option value="upi">UPI</option></UiSelect></label>
              </div>

              {formData.paymentmethod === 'upi' && (
                <div className="upi-qr-box">
                  <h4 className="upi-qr-title">Scan to pay with UPI</h4>
                  {getUpiQrUrl() ? (
                    <>
                      <img src={getUpiQrUrl()} alt="UPI QR" className="upi-qr-img" onError={e=>e.target.style.display='none'} />
                      <p className="upi-qr-amount">Show this QR to the student. Amount: <b>{currency(formData.receivedAmount || 0)}</b></p>
                      <button type="button" className="upi-view-btn" onClick={()=>window.open(getUpiQrUrl(), '_blank', 'noopener,noreferrer')}>View full QR</button>
                    </>
                  ) : (
                    <div className="upi-no-qr">No UPI QR uploaded. Go to Settings &gt; QR Management to upload one.</div>
                  )}
                </div>
              )}

              <label>Transaction ID<input value={formData.transaction_id} onChange={e=>setFormData({...formData, transaction_id:e.target.value})} placeholder="Optional" /></label>
              <div className="modal-actions"><button type="button" className="cancel-btn" onClick={()=>setShowAddModal(false)} disabled={savingPayment}>Cancel</button><button type="submit" className="save-btn" disabled={savingPayment||studentLoading}>{savingPayment?'Saving...':'Save Payment'}</button></div>
            </form>
          </section>
        </div>
      )}

      {showStudentModal && (
        <div className="modal-backdrop dark" onMouseDown={e=> e.target===e.currentTarget && setShowStudentModal(false)}>
          <section className="student-modal">
            <div className="student-modal-header"><h2>Choose Student</h2><button type="button" aria-label="Close student picker" onClick={()=>setShowStudentModal(false)}>{'\u00D7'}</button></div>
            <div className="student-search"><input placeholder="Search student..." value={studentSearch} onChange={e=>setStudentSearch(e.target.value)} /></div>
            <div className="student-list">
              <div className="student-item" onClick={()=>{ const upd={...filters, studentId:'', studentName:''}; setFilters(upd); setCurrentPage(1); setShowStudentModal(false); if(showAddModal) handleStudentChange({target:{value:''}}); else fetchLive(upd,filter,searchQuery); }}><b>All Students</b><small>Show all payments</small></div>
              {filteredStudents.map(s=>{ const id=s.id||s.student_id||s.studentid||s.studentId||s._id; const name=s.name||s.student_name||s.studentname||'Unknown'; return <div key={id} className="student-item" onClick={()=>{ if(showAddModal){ setShowStudentModal(false); handleStudentChange({target:{value:id}}); setFilters(prev=>({...prev, studentName:name})); } else { const upd={...filters, studentId:id, studentName:name}; setFilters(upd); setCurrentPage(1); setShowStudentModal(false); fetchLive(upd,filter,searchQuery); } }}><div><b>{name}</b><small>{s.email||s.phone||''} | {seatLibraryMap[String(s.seatid||s.seat_id||s.seatId)]||s.library_name||s.libraryName||''}</small></div><button type="button">Select</button></div> })}
            </div>
          </section>
        </div>
      )}

      {receiptPickerOpen && <div className="modal-backdrop" onMouseDown={e=> e.target===e.currentTarget && onCloseReceiptPicker()}><section className="student-modal"><div className="student-modal-header"><div><span className="eyebrow">PAYMENT RECORDS</span><h2>Download a receipt</h2></div><button type="button" aria-label="Close receipt picker" onClick={onCloseReceiptPicker}>{'\u00D7'}</button></div><div className="student-list">{payments.slice(0,10).map(p=>{ const pid=getReceiptId(p); const name=p.student_name||p.studentname||p.studentName||'Unknown'; return <div className="student-item" key={pid}><div><b>{name}</b><small>{getLibraryName(p)} | {currency(p.amount)}</small></div><button type="button" onClick={()=>handleReceipt(p)} disabled={String(downloadingReceiptId)===String(pid)}>{String(downloadingReceiptId)===String(pid)?'Working...':'Download'}</button></div> })}</div></section></div>}
    </div>
  );
}
